import React, { useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, PackageIcon, ClockIcon, CheckCircleIcon, AlertTriangleIcon, FileTextIcon, PercentIcon, DollarSignIcon, HashIcon } from 'lucide-react';
import { PurchaseOrderLine, ReceiptMode, ReceiptHistoryItem } from './ReceiptingView';
import { formatCurrency } from '../utils/currencyFormatter';
interface ReceiptLineCardProps {
  line: PurchaseOrderLine;
  currency: string;
  receiptEntry: {
    mode: ReceiptMode;
    amount: number;
    notes: string;
  };
  error?: string;
  onModeChange: (mode: ReceiptMode) => void;
  onAmountChange: (amount: number) => void;
  onNotesChange: (notes: string) => void;
  locale?: string;
}
export function ReceiptLineCard({
  line,
  currency,
  receiptEntry,
  error,
  onModeChange,
  onAmountChange,
  onNotesChange,
  locale = 'en-US'
}: ReceiptLineCardProps) {
  const [historyExpanded, setHistoryExpanded] = useState(false);
  // Calculate remaining quantities
  const remainingQuantity = line.quantity - line.receivedToDate;
  const remainingPercentage = remainingQuantity / line.quantity * 100;
  const remainingValue = line.totalPrice - line.receivedValueToDate;
  // Calculate progress percentage for the progress bar
  const progressPercentage = line.receivedToDate / line.quantity * 100;
  // Get icon based on line type
  const getLineTypeIcon = () => {
    return line.type === 'Item' ? <PackageIcon className="h-5 w-5 text-blue-500" data-id="element-14748" /> : <ClockIcon className="h-5 w-5 text-purple-500" data-id="element-14749" />;
  };
  // Get available modes based on line type
  const getAvailableModes = (): ReceiptMode[] => {
    if (line.type === 'Item') {
      return ['Quantity', 'Percentage', 'Value'];
    } else {
      return ['Percentage', 'Value'];
    }
  };
  // Get icon for the selected mode
  const getModeIcon = (mode: ReceiptMode) => {
    switch (mode) {
      case 'Quantity':
        return <HashIcon className="h-4 w-4" data-id="element-14750" />;
      case 'Percentage':
        return <PercentIcon className="h-4 w-4" data-id="element-14751" />;
      case 'Value':
        return <DollarSignIcon className="h-4 w-4" data-id="element-14752" />;
    }
  };
  // Format the display value based on mode
  const formatDisplayValue = (value: number, mode: ReceiptMode): string => {
    switch (mode) {
      case 'Quantity':
        return `${value} ${line.uom}`;
      case 'Percentage':
        return `${value}%`;
      case 'Value':
        return formatCurrency(value, currency, locale);
    }
  };
  // Get placeholder text based on mode
  const getPlaceholder = (): string => {
    switch (receiptEntry.mode) {
      case 'Quantity':
        return `Enter quantity (max ${remainingQuantity} ${line.uom})`;
      case 'Percentage':
        return `Enter percentage (max ${remainingPercentage.toFixed(2)}%)`;
      case 'Value':
        return `Enter value (max ${formatCurrency(remainingValue, currency, locale)})`;
    }
  };
  // Get input type based on mode
  const getInputType = (): string => {
    return 'number';
  };
  // Get input step based on mode
  const getInputStep = (): string => {
    switch (receiptEntry.mode) {
      case 'Quantity':
        return '1';
      case 'Percentage':
        return '0.01';
      case 'Value':
        return '0.01';
    }
  };
  // Get max value based on mode
  const getMaxValue = (): number => {
    switch (receiptEntry.mode) {
      case 'Quantity':
        return remainingQuantity;
      case 'Percentage':
        return remainingPercentage;
      case 'Value':
        return remainingValue;
    }
  };
  // Convert entered value to quantity, percentage, and value for display
  const calculateEquivalentValues = () => {
    if (receiptEntry.amount <= 0) {
      return {
        quantity: 0,
        percentage: 0,
        value: 0
      };
    }
    let quantity = 0;
    let percentage = 0;
    let value = 0;
    switch (receiptEntry.mode) {
      case 'Quantity':
        quantity = receiptEntry.amount;
        percentage = quantity / line.quantity * 100;
        value = quantity * line.unitPrice;
        break;
      case 'Percentage':
        percentage = receiptEntry.amount;
        quantity = percentage / 100 * line.quantity;
        value = percentage / 100 * line.totalPrice;
        break;
      case 'Value':
        value = receiptEntry.amount;
        percentage = value / line.totalPrice * 100;
        quantity = value / line.unitPrice;
        break;
    }
    return {
      quantity,
      percentage,
      value
    };
  };
  const equivalentValues = calculateEquivalentValues();
  return <div className={`border rounded-lg overflow-hidden ${error ? 'border-red-300' : 'border-gray-200'}`} data-id="element-14753">
      <div className="px-4 py-4 sm:px-6 bg-gray-50 border-b border-gray-200" data-id="element-14754">
        <div className="flex flex-wrap items-center justify-between" data-id="element-14755">
          <div className="flex items-center" data-id="element-14756">
            {getLineTypeIcon()}
            <h4 className="ml-2 text-lg font-medium text-gray-900" data-id="element-14757">
              {line.itemCode}: {line.description}
            </h4>
            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-14758">
              {line.type}
            </span>
          </div>
          <div className="flex items-center mt-2 sm:mt-0" data-id="element-14759">
            <button type="button" onClick={() => setHistoryExpanded(!historyExpanded)} className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14760">
              {historyExpanded ? <>
                  <ChevronUpIcon className="h-4 w-4 mr-1" data-id="element-14761" />
                  Hide History
                </> : <>
                  <ChevronDownIcon className="h-4 w-4 mr-1" data-id="element-14762" />
                  Show History ({line.receiptHistory.length})
                </>}
            </button>
          </div>
        </div>
      </div>
      <div className="px-4 py-4 sm:px-6" data-id="element-14763">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-14764">
          {/* Order Information */}
          <div className="bg-gray-50 p-3 rounded-md" data-id="element-14765">
            <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-14766">
              Order Information
            </h5>
            <div className="space-y-2" data-id="element-14767">
              <div className="flex justify-between" data-id="element-14768">
                <span className="text-sm text-gray-500" data-id="element-14769">Ordered:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-14770">
                  {formatDisplayValue(line.quantity, 'Quantity')}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-14771">
                <span className="text-sm text-gray-500" data-id="element-14772">Unit Price:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-14773">
                  {formatCurrency(line.unitPrice, currency, locale)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-14774">
                <span className="text-sm text-gray-500" data-id="element-14775">Total Value:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-14776">
                  {formatCurrency(line.totalPrice, currency, locale)}
                </span>
              </div>
            </div>
          </div>
          {/* Receipt Status */}
          <div className="bg-gray-50 p-3 rounded-md" data-id="element-14777">
            <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-14778">
              Receipt Status
            </h5>
            <div className="space-y-2" data-id="element-14779">
              <div className="flex justify-between" data-id="element-14780">
                <span className="text-sm text-gray-500" data-id="element-14781">Received to Date:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-14782">
                  {formatDisplayValue(line.receivedToDate, 'Quantity')}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-14783">
                <span className="text-sm text-gray-500" data-id="element-14784">Value Received:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-14785">
                  {formatCurrency(line.receivedValueToDate, currency, locale)}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-14786">
                <span className="text-sm text-gray-500" data-id="element-14787">Remaining:</span>
                <span className="text-sm font-medium text-gray-900" data-id="element-14788">
                  {formatDisplayValue(remainingQuantity, 'Quantity')}
                </span>
              </div>
            </div>
            <div className="mt-3" data-id="element-14789">
              <div className="relative pt-1" data-id="element-14790">
                <div className="flex mb-2 items-center justify-between" data-id="element-14791">
                  <div data-id="element-14792">
                    <span className="text-xs font-semibold inline-block text-indigo-600" data-id="element-14793">
                      {progressPercentage.toFixed(0)}% Complete
                    </span>
                  </div>
                </div>
                <div className="overflow-hidden h-2 text-xs flex rounded bg-indigo-200" data-id="element-14794">
                  <div style={{
                  width: `${progressPercentage}%`
                }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-500" data-id="element-14795"></div>
                </div>
              </div>
            </div>
          </div>
          {/* Receipt Entry */}
          <div className="border border-gray-200 p-3 rounded-md w-full" data-id="element-14796">
            <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-14797">
              Enter Receipt
            </h5>
            {/* Mode Selection */}
            <div className="mb-3" data-id="element-14798">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14799">
                Receipt Mode
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-14800">
                {getAvailableModes().map(mode => <button key={mode} type="button" onClick={() => onModeChange(mode)} className={`inline-flex items-center px-3 py-1.5 border text-sm font-medium rounded-md ${receiptEntry.mode === mode ? 'bg-indigo-100 border-indigo-300 text-indigo-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-14801">
                    {getModeIcon(mode)}
                    <span className="ml-1" data-id="element-14802">{mode}</span>
                  </button>)}
              </div>
            </div>
            {/* Amount Input */}
            <div className="mb-3" data-id="element-14803">
              <label htmlFor={`amount-${line.id}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14804">
                Amount to Receive
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-14805">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-14806">
                  {getModeIcon(receiptEntry.mode)}
                </div>
                <input type={getInputType()} id={`amount-${line.id}`} value={receiptEntry.amount} onChange={e => onAmountChange(parseFloat(e.target.value) || 0)} placeholder={getPlaceholder()} step={getInputStep()} min="0" max={getMaxValue()} className={`focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md ${error ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500' : ''}`} data-id="element-14807" />
              </div>
              {error && <p className="mt-1 text-sm text-red-600" data-id="element-14808">{error}</p>}
            </div>
            {/* Equivalent Values */}
            {receiptEntry.amount > 0 && <div className="mb-3 p-2 bg-gray-50 rounded-md" data-id="element-14809">
                <h6 className="text-xs font-medium text-gray-500 mb-1" data-id="element-14810">
                  Equivalent Values:
                </h6>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs" data-id="element-14811">
                  <div className="overflow-hidden text-ellipsis" data-id="element-14812">
                    <span className="text-gray-500" data-id="element-14813">Qty: </span>
                    <span className="font-medium" data-id="element-14814">
                      {equivalentValues.quantity.toFixed(2)} {line.uom}
                    </span>
                  </div>
                  <div className="overflow-hidden text-ellipsis" data-id="element-14815">
                    <span className="text-gray-500" data-id="element-14816">%: </span>
                    <span className="font-medium" data-id="element-14817">
                      {equivalentValues.percentage.toFixed(2)}%
                    </span>
                  </div>
                  <div className="overflow-hidden text-ellipsis" data-id="element-14818">
                    <span className="text-gray-500" data-id="element-14819">Value: </span>
                    <span className="font-medium" data-id="element-14820">
                      {formatCurrency(equivalentValues.value, currency, locale)}
                    </span>
                  </div>
                </div>
              </div>}
            {/* Notes */}
            <div data-id="element-14821">
              <label htmlFor={`notes-${line.id}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-14822">
                Notes
                {line.receivedToDate + (receiptEntry.mode === 'Quantity' ? receiptEntry.amount : 0) === line.quantity && <span className="text-red-500 ml-1" data-id="element-14823">*</span>}
              </label>
              <textarea id={`notes-${line.id}`} value={receiptEntry.notes} onChange={e => onNotesChange(e.target.value)} rows={2} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Enter any notes about this receipt" data-id="element-14824" />
              {line.receivedToDate + (receiptEntry.mode === 'Quantity' ? receiptEntry.amount : 0) === line.quantity && <p className="mt-1 text-xs text-gray-500" data-id="element-14825">
                  <AlertTriangleIcon className="inline h-3 w-3 text-amber-500 mr-1" data-id="element-14826" />
                  Notes are required for final receipt
                </p>}
            </div>
          </div>
        </div>
      </div>
      {/* Receipt History */}
      {historyExpanded && <div className="px-4 py-3 sm:px-6 bg-gray-50 border-t border-gray-200" data-id="element-14827">
          <h5 className="text-sm font-medium text-gray-700 mb-3" data-id="element-14828">
            Receipt History
          </h5>
          {line.receiptHistory.length === 0 ? <p className="text-sm text-gray-500 italic" data-id="element-14829">
              No receipt history available
            </p> : <div className="overflow-x-auto" data-id="element-14830">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-14831">
                <thead className="bg-gray-100" data-id="element-14832">
                  <tr data-id="element-14833">
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14834">
                      Receipt ID
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14835">
                      Date
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14836">
                      Mode
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14837">
                      Quantity
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14838">
                      Percentage
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14839">
                      Value
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14840">
                      Status
                    </th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14841">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-14842">
                  {line.receiptHistory.map(receipt => <tr key={receipt.id} className="hover:bg-gray-50" data-id="element-14843">
                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-14844">
                        {receipt.id}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-14845">
                        {receipt.date}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-14846">
                        {receipt.mode}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-14847">
                        {receipt.amountReceived} {line.uom}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-14848">
                        {receipt.percentageReceived.toFixed(2)}%
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-14849">
                        {formatCurrency(receipt.valueReceived, currency, locale)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-14850">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${receipt.status === 'Submitted' ? 'bg-green-100 text-green-800' : receipt.status === 'Draft' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`} data-id="element-14851">
                          {receipt.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-14852">
                        {receipt.notes || '-'}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>}
    </div>;
}